import { Link } from 'react-router-dom';
import type { Drama } from '@/types/drama';

interface DetailHeroProps {
  drama: Drama;
  isFav: boolean;
  onToggleFav: () => void;
  onShare: () => void;
}

export default function DetailHero({ drama, isFav, onToggleFav, onShare }: DetailHeroProps) {
  const meta = [
    { label: '导演', value: drama.director },
    { label: '主演', value: drama.actors.join(' / ') },
    { label: '类型', value: drama.genres.join(' · ') },
    { label: '地区', value: drama.region },
    { label: '年份', value: `${drama.year}` },
    { label: '集数', value: `更新至 ${drama.latestEpisode} 集 / 全 ${drama.episodes} 集` },
    { label: '更新时间', value: drama.updateTime },
  ];

  return (
    <section className="bg-background-100 border-b border-background-200">
      <div className="mx-auto flex max-w-6xl flex-col gap-6 px-4 py-8 md:flex-row md:gap-8 md:px-6 md:py-10">
        <div className="w-full shrink-0 md:w-64">
          <div className="relative w-full overflow-hidden rounded-lg border border-background-200 bg-background-200 md:aspect-[2/3]">
            <img
              src={drama.poster}
              alt={`${drama.title} 电视剧海报`}
              title={`${drama.title} 详情介绍`}
              className="h-full w-full object-cover object-top"
            />
          </div>
        </div>

        <div className="min-w-0 flex-1">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            {drama.title}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
            <span className="flex items-center gap-1 font-semibold text-accent-600">
              <i className="ri-star-fill"></i>
              {drama.rating.toFixed(1)}
            </span>
            <div className="flex flex-wrap gap-1.5">
              {drama.tags.map((t) => (
                <span key={t} className="rounded-full bg-accent-100 px-2.5 py-0.5 text-xs text-accent-900">
                  {t}
                </span>
              ))}
            </div>
          </div>

          <dl className="mt-5 grid grid-cols-1 gap-x-6 gap-y-2.5 text-sm sm:grid-cols-2">
            {meta.map((m) => (
              <div key={m.label} className="flex gap-2">
                <dt className="shrink-0 text-foreground-500">{m.label}</dt>
                <dd className="min-w-0 text-foreground-900">{m.value}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/play/${drama.id}/1`}
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-play-circle-line text-lg"></i>
              立即观看
            </Link>
            <button
              type="button"
              onClick={onToggleFav}
              className={`flex items-center gap-2 whitespace-nowrap rounded-md border px-5 py-2.5 text-sm font-medium transition-colors cursor-pointer ${
                isFav
                  ? 'border-accent-500 bg-accent-500 text-foreground-950'
                  : 'border-background-300 text-foreground-700 hover:border-primary-300'
              }`}
            >
              <i className={isFav ? 'ri-heart-fill' : 'ri-heart-line'}></i>
              {isFav ? '已收藏' : '收藏'}
            </button>
            <button
              type="button"
              onClick={onShare}
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 cursor-pointer"
            >
              <i className="ri-share-line"></i>
              分享
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}